<template>
  <div>
    <!-- Drawer -->
    <div id="kt_drawer_example" class="drawer bg-white" :class="{
      active: isDrawerActive,
      'animate__animated animate__fadeInRight': isDrawerActive,
    }">
      <div class="drawer-header">
        <h5>{{ drawerTitle }}</h5>
        <slot name="drawerHeader"></slot>
        <button id="kt_drawer_example_close" class="btn btn-icon btn-light" @click="hideDrawer">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <div class="drawer-body">
        <div class="drawer-content">
          <slot name="drawerContent"></slot>
        </div>
      </div>
    </div>

    <!-- Overlay -->
    <div class="drawer-overlay"
      :class="{ active: isDrawerActive, 'animate__animated animate__fadeInLeft': isDrawerActive }">
      <slot name="drawerOverlay"></slot>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps, watch } from "vue";
import "animate.css"; // Import animate.css for animations
const emit = defineEmits(['hideDrawerToggle', 'hideDrawer'])
const props = defineProps({
  isDrawerActive: {
    type: Boolean,
    default: false,
  },
  drawerTitle: {
    type: String,
    default: "Drawer Title",
  },
});
let isDrawerActive = ref(props.isDrawerActive);
let drawerTitle = ref(props.drawerTitle);
onMounted(() => {
  // Initialize drawer state from props
  isDrawerActive.value = props.isDrawerActive;
  drawerTitle.value = props.drawerTitle;
});

watch(() => props.isDrawerActive, (newValue) => {
  isDrawerActive.value = newValue;
});

// Show drawer
const showDrawer = () => {
  isDrawerActive.value = true;
};

// Hide drawer
const hideDrawer = () => {
  isDrawerActive.value = false;
  emit('hideDrawerToggle');
};

// Expose methods for programmatic control
window.showDrawer = showDrawer;
window.hideDrawer = hideDrawer;

// Clean up global methods on component unmount
onUnmounted(() => {
  delete window.showDrawer;
  delete window.hideDrawer;
});
</script>

<style scoped>
.drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100%;
  background-color: #fff;
  box-shadow: -5px 0 10px rgba(0, 0, 0, 0.1);
  transform: translateX(100%);
  /* Start off-screen */
  transition: transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 1050;
  /* Matches Metronic 7’s z-index */
  will-change: transform;
  /* Optimize for animation */
}

.drawer.active {
  transform: translateX(0);
  /* Slide in */
}

@media (min-width: 992px) {
  .drawer {
    width: 500px;
    /* Responsive width like Metronic 8 */
  }
}

.drawer-header {
  padding: 1.5rem;
  border-bottom: 1px solid #ebedf2;
  /* Metronic 7’s border color */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.drawer-body {
  padding: 1.5rem;
}

.drawer-content {
  opacity: 0;
  transform: translateX(20px);
  /* Slight offset for content */
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;
  transition-delay: 0.1s;
  /* Delay content animation */
}

.drawer.active .drawer-content {
  opacity: 1;
  transform: translateX(0);
}

/* Overlay for background dimming */
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
  opacity: 0;
  transition: opacity 0.4s ease-out;
  transition-delay: 0.05s;
  /* Slight delay for overlay */
  pointer-events: none;
}

.drawer-overlay.active {
  opacity: 1;
  pointer-events: auto;
}
</style>